import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useTable, useSortBy, usePagination } from "react-table";

import Card from "../../components/ui/Card";
import ErrorState from "../../components/ui/ErrorState";
import RoleBadge from "../../components/ui/RoleBadge";
import SortIndicator from "../../components/ui/SortIndicator";
import Spinner from "../../components/ui/Spinner";
import StatusDot from "../../components/ui/StatusDot";
import {
  useMembers,
  useAccessGrants,
  describeGrant,
  grantConveysSomething,
  revokeInvitation,
  pendingGrantKey,
  apiMessage,
  MEMBERS_QUERY_KEY,
  GRANTS_QUERY_KEY,
  type AccessGrant,
  type Member,
} from "../../services/people";

import { TIER_RANK } from "../../services/roles";
import Button from "../../components/ui/Button";
import InviteUserModal from "../../components/ui/InviteUserModal";

import "../Events/tableStyle.scss";

/**
 * Everyone in the organization, with their roles and what they can reach.
 *
 * Read-only for now, deliberately. Inviting, re-roling and editing a person's
 * properties each need their own editor, and a list that shows the truth is worth
 * shipping before the controls that change it -- not least because an admin needs
 * to see who has access before deciding what to change.
 *
 * **Built on the same table as the Buildings and Sensors views, and that is the
 * point.** A first version hand-rolled a dark `<table>` with its own padding and
 * borders, which made this page read as a different product: `.cts-table-card` is
 * a WHITE data surface on the dark dashboard, so a dark table in its place
 * differed in background, row height, header treatment, striping, hover colour
 * and the drill-in chevron all at once. Sorting and paging were missing too.
 * Importing `tableStyle.scss` and driving it with react-table v7 is how those
 * stay in step rather than being re-approximated here.
 */
const PAGE_SIZE = 10;

export default function PeoplePage() {
  const navigate = useNavigate();
  const members = useMembers();
  const grants = useAccessGrants();

  /**
   * Grants keyed by user id. Absence is meaningful: somebody with no grant row has
   * no access once their tenant enforces scope, which is why the cell says "No
   * access" rather than being left blank.
   */
  const grantsByUser = useMemo(() => {
    const map = new Map<string, AccessGrant>();
    for (const grant of grants.data ?? []) {
      if (grant.user_id) map.set(grant.user_id, grant);
    }
    return map;
  }, [grants.data]);

  const grantsKnown = !grants.isError && !grants.isPending;

  const data = useMemo(
    () =>
      (members.data ?? []).map((member: Member) => {
        const grant = grantsByUser.get(member.userId);

        return {
          member,
          grant,
          // Sort keys live on the row rather than in a custom sort function:
          // the column sorts on a string the cell also renders, so what is
          // sorted and what is read cannot drift apart.
          person: member.name || member.email || member.userId,
          // The SORT key is the tier, and it sorts by seniority rather than
          // alphabetically -- "Administrator, Manager, User" happens to be
          // alphabetical, but that is a coincidence not worth relying on. The
          // rendered chip reads the same tier, so sorted and rendered cannot drift.
          tier: member.tier ?? "None",
          tierRank: TIER_RANK[member.tier ?? "None"] ?? 0,
          tierLabel: member.tierLabel ?? "",
          accessLabel: grantsKnown ? describeGrant(grant) : "",
        };
      }),
    [members.data, grantsByUser, grantsKnown]
  );

  const columns = useMemo(
    () => [
      {
        Header: "Person",
        accessor: "person",
        textCell: true,
        Cell: ({ row }: any) => {
          const { member } = row.original;
          return (
            <>
              <div>{member.name || member.email || member.userId}</div>
              {/* Only when it adds something. A pending row has no display name,
                  so the API fills it with the address -- and without this the
                  email rendered twice, once as the name and once beneath it. */}
              {member.name && member.email && member.name !== member.email && (
                <div className="text-ink-2 text-xs">{member.email}</div>
              )}
              {/* Said on the row itself rather than only in a column, because
                  the difference that matters is that there is no account yet --
                  which changes what every other cell means. */}
              {member.pending && (
                <div className="text-ink-2 text-xs flex items-center gap-2">
                  <span>Invited &middot; hasn't accepted yet</span>
                  {/* Beside the address, where the person is. Withdrawing is
                      about the invitation, not about what it grants, so the
                      access column was the wrong home for it. */}
                  <button
                    type="button"
                    className="text-primary hover:underline disabled:opacity-50"
                    disabled={revoke.isPending}
                    onClick={(e) => {
                      e.stopPropagation();
                      const who = member.email || "this invitation";
                      if (window.confirm(`Withdraw the invitation to ${who}?`)) {
                        revoke.mutate(member.invitationId);
                      }
                    }}
                  >
                    {revoke.isPending ? "Withdrawing…" : "Withdraw"}
                  </button>
                </div>
              )}
            </>
          );
        },
      },
      {
        Header: "Role",
        accessor: "tierRank",
        // Sorts by seniority, which is what somebody scanning the column wants,
        // and descending first so administrators surface rather than sinking.
        sortDescFirst: true,
        Cell: ({ row }: any) => {
          const { tier, tierLabel } = row.original;

          return <RoleBadge tier={tier} label={tierLabel} />;
        },
      },
      {
        Header: "Sensor access",
        accessor: "accessLabel",
        Cell: ({ row }: any) => {
          const { member, grant } = row.original;

          // A pending row has no account, so it has no grant and no detail page.
          // Withdrawing the invitation is the only thing that can be done to it,
          // and it is the thing an admin needs: a pending invitation blocks a
          // second one to the same address.
          if (member.pending) {
            // A pending grant is keyed on the invited ADDRESS, not a user id --
            // there is no user id yet -- so the row's own grant is looked up by
            // that key rather than by `user_id` like every other row.
            const chosen = member.email
              ? grantsByUser.get(pendingGrantKey(member.email))
              : undefined;

            return (
              // inline-flex in a span, matching the sibling cell below. A
              // block-level flex container fills the cell, so the column's
              // centring had nothing to act on and the contents sat left while
              // every other row's were centred.
              <span className="inline-flex items-center gap-3">
                <span className="text-ink-2 text-sm">
                  {grantsKnown && chosen ? describeGrant(chosen) : "No access yet"}
                </span>
              </span>
            );
          }

          if (!grantsKnown) {
            return <span className="text-ink-3">—</span>;
          }

          return (
            <span className="inline-flex items-center gap-2">
              {/* The tone follows the same statement the text does. Asking
                  `group_ids.length > 0` here was a second definition of "conveys
                  nothing", and it disagreed: a grant of individual sensors and no
                  property showed a muted dot -- which reads as no access -- beside
                  text saying "+1 sensor". */}
              <StatusDot
                tone={
                  !grantConveysSomething(grant)
                    ? "muted"
                    : grant?.all_properties
                      ? "success"
                      : "info"
                }
              />
              <span>{describeGrant(grant)}</span>
            </span>
          );
        },
      },
    ],
    // grantsByUser too, not just grantsKnown. The pending row's cell looks a
    // grant up by the invited ADDRESS rather than taking it from the row, so it
    // closes over this map -- and with only grantsKnown here the closure was
    // stale: access saved for an invitee stayed "No access yet" until something
    // else happened to re-memo. The cache had it; the cell was looking at an
    // older copy.
    [grantsKnown, grantsByUser]
  );

  // react-table v7's shipped types describe the CORE instance only: the fields
  // its plugins add -- usePagination's page/gotoPage/canNextPage, useSortBy's
  // isSorted -- exist at runtime but need the consumer to augment its
  // interfaces to be visible. The insights tables sidestep that by being .js.
  // Augmenting globally for one page would put a second, partial description of
  // this library in the tree, so the instance is read untyped here instead and
  // the cell renderers take `any` rows, which is the shape those tables already
  // pass. The columns array is cast for the same reason: `textCell` is a custom
  // field the core Column type does not know about, which this component reads
  // to tag a cell `cts-text-cell` so only text-heavy columns ellipsize.
  const table = useTable(
    {
      columns: columns as never,
      data,
      initialState: { pageSize: PAGE_SIZE, sortBy: [{ id: "person" }] } as never,
    },
    useSortBy,
    usePagination
  ) as any;

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    state: { pageIndex },
  } = table;

  // Pad the page to a fixed row count so the table height does not collapse on
  // a short last page, matching the insights tables.
  const [inviting, setInviting] = useState(false);
  const [revokeError, setRevokeError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const revoke = useMutation({
    mutationFn: async (invitationId: string) => {
      await revokeInvitation(invitationId);
    },
    onSuccess: () => {
      setRevokeError(null);
      // Not awaited: the withdrawal has landed, and the list refresh is a
      // separate read that can fail on its own account.
      //
      // Grants too: withdrawing an invitation deletes the access chosen for it
      // (REQ-ACL-23), so a list that still showed it would be describing a row
      // the server has removed.
      void queryClient.invalidateQueries({ queryKey: MEMBERS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: GRANTS_QUERY_KEY });
    },
    onError: (e) =>
      setRevokeError(
        apiMessage(e, "We couldn't withdraw that invitation. Please try again.")
      ),
  });

  const paddedPage = useMemo(() => {
    const padded = [...page];
    while (padded.length < PAGE_SIZE) padded.push(null);
    return padded;
  }, [page]);

  if (members.isPending) {
    return (
      <>
        <Header onInvite={() => setInviting(true)} />
        <InviteUserModal open={inviting} onClose={() => setInviting(false)} />
        <div className="flex flex-col items-center gap-4 py-16">
          <Spinner size={96} />
          {/* The members endpoint reads each person's roles separately, so this
              is genuinely slow for a real tenant. Saying so beats looking
              stuck. */}
          <p className="text-ink-3 text-sm">
            Loading people. This can take a few seconds for a large organization.
          </p>
        </div>
      </>
    );
  }

  if (members.isError) {
    return (
      <>
        <Header onInvite={() => setInviting(true)} />
        <InviteUserModal open={inviting} onClose={() => setInviting(false)} />
        <ErrorState
          title="We couldn't load your people"
          message="Please try again. If this keeps happening, the directory service may be unavailable."
          onRetry={() => members.refetch()}
        />
      </>
    );
  }

  return (
    <>
      <Header count={data.length} onInvite={() => setInviting(true)} />

      <InviteUserModal open={inviting} onClose={() => setInviting(false)} />


      {revokeError && (
        <Card className="mb-4" tone="dark">
          <p className="text-alert-critical text-sm" role="alert">
            {revokeError}
          </p>
        </Card>
      )}

      {grants.isError && (
        <Card className="mb-4" tone="dark">
          <p className="text-alert-warning text-sm">
            Roles are shown below, but we couldn't load who can reach which
            sensors. The access column is incomplete.
          </p>
        </Card>
      )}

      {data.length === 0 ? (
        /* The list comes back with everyone in the tenant, the signed-in admin
           included, so an empty one is not "only you" -- it is a directory that
           answered with nobody, which is a different thing and worth not
           mis-stating on the page whose job is to say who can sign in. */
        <ErrorState
          title="Nobody to show"
          message="This organization's directory came back empty. If that looks wrong, reload to try again."
          onRetry={() => members.refetch()}
        />
      ) : (
        <div className="cts-table-card">
          <table className="cts-table" {...getTableProps()}>
            <thead>
              {headerGroups.map((headerGroup: any) => {
                const { key: hgKey, ...hgRest } =
                  headerGroup.getHeaderGroupProps();
                return (
                  <tr key={hgKey} {...hgRest}>
                    {headerGroup.headers.map((column: any) => {
                      const sortState = column.isSorted
                        ? column.isSortedDesc
                          ? "desc"
                          : "asc"
                        : "none";
                      const { key: colKey, ...colRest } = column.getHeaderProps(
                        column.getSortByToggleProps({
                          title: `Sort by ${column.render("Header")}`,
                        })
                      );
                      return (
                        <th key={colKey} {...colRest}>
                          <span className="inline-flex items-center">
                            {column.render("Header")}
                            <span className="cts-sort">
                              <SortIndicator state={sortState} />
                            </span>
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                );
              })}
            </thead>
            <tbody {...getTableBodyProps()}>
              {paddedPage.map((row: any, idx: number) => {
                if (!row) {
                  return (
                    <tr key={`empty-${idx}`} className="cts-empty-row">
                      <td colSpan={columns.length}>&nbsp;</td>
                    </tr>
                  );
                }
                prepareRow(row);
                const { key: rowKey, ...rowRest } = row.getRowProps();
                return (
                  <tr
                    key={rowKey}
                    {...rowRest}
                    // A pending row has no userId, so there is nothing to
                    // open. Navigating anyway sent an admin to a detail page
                    // that answered "We couldn't find that person" about
                    // somebody they had just invited.
                    // Both kinds open: a member by their id, an invitee by the
                    // invitation's. A pending row used not to open at all, which
                    // left its only actions stranded in the access column.
                    onClick={() => {
                      const { userId, pending, invitationId } = row.original.member;

                      if (pending && invitationId) {
                        navigate(
                          `/admin/people/invitation/${encodeURIComponent(invitationId)}`
                        );
                        return;
                      }

                      if (userId) {
                        navigate(`/admin/people/${encodeURIComponent(userId)}`);
                      }
                    }}
                  >
                    {row.cells.map((cell: any) => {
                      const { key: cellKey, ...cellRest } = cell.getCellProps();
                      return (
                        <td
                          key={cellKey}
                          {...cellRest}
                          className={
                            cell.column.textCell ? "cts-text-cell" : ""
                          }
                        >
                          {cell.render("Cell")}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>

          {pageOptions.length > 1 && (
            <div className="cts-pagination">
              <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
                {"«"}
              </button>
              <button onClick={() => previousPage()} disabled={!canPreviousPage}>
                {"‹"}
              </button>
              {Array.from({ length: Math.min(5, pageOptions.length) }, (_, i) => {
                const pageNumber = pageIndex + 1 + i;
                const isDisabled = pageNumber > pageOptions.length;
                return (
                  <button
                    key={pageNumber}
                    disabled={isDisabled}
                    onClick={() => !isDisabled && gotoPage(pageNumber - 1)}
                    style={
                      pageNumber - 1 === pageIndex
                        ? {
                            background: "rgba(187,218,98,0.2)",
                            borderColor: "#BBDA62",
                          }
                        : undefined
                    }
                  >
                    {pageNumber}
                  </button>
                );
              })}
              <button onClick={() => nextPage()} disabled={!canNextPage}>
                {"›"}
              </button>
              <button
                onClick={() => gotoPage(pageOptions.length - 1)}
                disabled={!canNextPage}
              >
                {"»"}
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}

function Header({
  count,
  onInvite,
}: {
  count?: number;
  onInvite: () => void;
}) {
  return (
    <div className="mb-6 flex items-start justify-between gap-4">
      <div>
        <h1 className="text-2xl font-semibold text-white">People</h1>
        <p className="text-ink-3 mt-1">
          Who can sign in to this organization, and which sensors they can reach.
          {count !== undefined && count > 0 &&
            ` ${count} ${count === 1 ? "person" : "people"}.`}
        </p>
      </div>

      {/* In the header, not in the loaded-list branch, so it is reachable while
          the list is loading, empty or errored -- an empty directory is when
          somebody most wants to invite the first person. No capability gate of
          its own: this whole page is already behind ManagePeople, and adding a
          second, different gate here would be a second answer to one question. */}
      <Button onClick={onInvite} className="shrink-0">
        Invite
      </Button>
    </div>
  );
}
